//#region IMPORTS
import {
  setOrderParams,
  clearOrderParams,
  fetchOrdersAsync,
  fetchApprovableOrdersAsync,
  fetchShippableOrdersAsync,
  fetchInProgressOrdersAsync,
  ordersSelectors,
} from "../../../features/orders/ordersSlice";
import {
  resolvePreset,
  BULK_FETCH_LIMIT,
  HANDLE_PRESET_KEY,
  PROMO_BASE_PARAMS,
  APPROVE_PRESET_KEY,
  DEFAULT_PRESET_KEY,
  SEND_PRESET_KEY,
  isNarrowEnoughForFullLoad,
} from "./promoPresets";
import { useAppDispatch, useAppSelector } from "../../../store/configureStore";
import { useHistory, useLocation } from "react-router-dom";
import { SHOP_TAB, TAB_LABELS } from "../tabs";
import { useEffect } from "react";
import usePickerManager from "../../../hooks/usePickerManager";
import PromoPresetPills from "./PromoPresetPills";
import OrdersContainer from "../OrdersContainer";
import useAdminManager from "../../../hooks/useAdminManager";
import useHQmanager from "../../../hooks/useHQmanager";
//#endregion

export default function PromoOrdersView() {
  //#region SETUP
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const {
    orders_loaded,
    order_params,
    orders_ordered,
    orders_pending,
    orders_inProgress,
    bulkRefreshing,
  } = useAppSelector((state) => state.orders);
  const { user } = useAppSelector((state) => state.account);

  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);

  const searchParams = new URLSearchParams(location.search);
  const rawPreset = searchParams.get("preset");
  const promoCode = searchParams.get("promo_code");
  const effectivePresetKey = rawPreset ?? DEFAULT_PRESET_KEY;
  const { preset, presetKey, filter } = resolvePreset(effectivePresetKey);

  const isHandlePreset =
    presetKey === HANDLE_PRESET_KEY && (isPicker || isAdmin);
  // Promo approve is HQ / admin only — pickers may not approve promo orders.
  const isApprovePreset =
    presetKey === APPROVE_PRESET_KEY && (isHq || isAdmin);
  const isSendPreset = presetKey === SEND_PRESET_KEY && (isPicker || isAdmin);

  // Preset-route guard: if the resolved preset is a flow preset (Approve / Manage
  // / Send) that this role may not use, it is off-limits. The role-gated booleans
  // above already encode "preset matches AND role allowed", so a flow key with a
  // false flag means the current role isn't allowed there. Covers:
  //   - picker / shop on Approve, HQ / shop on Manage / Send, etc.
  // Overview presets (backorder / original / dotted backorder filters) are open
  // to everyone, so they never trip this. Off-limits → bounce to the role default.
  const presetNotAllowed =
    (presetKey === APPROVE_PRESET_KEY && !isApprovePreset) ||
    (presetKey === HANDLE_PRESET_KEY && !isHandlePreset) ||
    (presetKey === SEND_PRESET_KEY && !isSendPreset);
  const workflowActive = isApprovePreset || isHandlePreset || isSendPreset;
  const orders = useAppSelector(ordersSelectors.selectAll);

  // Bulk actions come with a client-side full load: every matching order is
  // fetched in one request and paginated in the browser, so a selection is kept
  // across pages. That only holds up on a list that is already narrow — see
  // `fullLoadAllowed` below; broader lists stay server-side paginated.
  const readOnly = presetKey === "original";
  // HQ only gets bulk (and client-side pagination) on the Approve preset.
  // Picker / admin: flow presets always; the manual path only once the list is
  // narrow enough to pull in full — same predicate as useBulkActionsAvailable,
  // so the checkbox column and the fetch strategy cannot drift apart.
  const fullLoadAllowed = isNarrowEnoughForFullLoad(order_params);
  const bulkAvailable = isHq
    ? isApprovePreset
    : workflowActive
      ? true
      : !readOnly && fullLoadAllowed;
  const desiredLimit = bulkAvailable
    ? BULK_FETCH_LIMIT
    : PROMO_BASE_PARAMS.limit;

  // The preset's own params — everything that is *not* a user filter. Clearing
  // all filters must land back on this, not on the generic order defaults:
  // those carry no preset filter and no sort.
  const presetBaseParams = {
    ...PROMO_BASE_PARAMS,
    ...filter,
    ...(preset?.paramsOverride ?? {}),
  };

  // On a fresh landing (no explicit preset):
  //  - picker / admin land on the Backorder overview (no auto-redirect)
  //  - HQ jumps straight to Approve when there is pending work
  const redirectTarget: string | null =
    rawPreset ? null : isHq && orders_pending > 0 ? APPROVE_PRESET_KEY : null;
  //#endregion

  //#region SIDE EFFECTS
  useEffect(() => {
    // Bounce a role off a preset it may not use, to its default landing — which
    // then resolves to that role's first flow step with work (or the overview).
    if (presetNotAllowed) {
      history.replace(`/orders?tab=${SHOP_TAB.promo}`);
      return;
    }
    if (redirectTarget) {
      history.replace(`/orders?tab=${SHOP_TAB.promo}&preset=${redirectTarget}`);
    }
  }, [presetNotAllowed, redirectTarget, history]);

  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    const presetBulk = isHq
      ? isApprovePreset
      : !!filter.order_status && !readOnly;
    dispatch(clearOrderParams());
    dispatch(
      setOrderParams({
        ...presetBaseParams,
        ...(promoCode ? { promo_code: promoCode } : {}),
        limit: presetBulk ? BULK_FETCH_LIMIT : PROMO_BASE_PARAMS.limit,
      }),
    );
  }, [dispatch, presetKey, promoCode, redirectTarget]);

  // Keep the fetch limit in sync when the status filter changes at runtime
  // through the regular filter bar (the preset effect above does not re-run).
  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    if (order_params.limit !== desiredLimit) {
      dispatch(setOrderParams({ limit: desiredLimit }));
    }
  }, [desiredLimit, order_params.limit, dispatch, redirectTarget]);

  // When a status change (bulk or per-row) empties the current flow list,
  // advance to the next flow pill with work (Approve → Manage → Send), else to
  // the Backorder overview. HQ has no Manage/Send, so HQ's Approve → Backorder.
  // Wait until the count refetches that follow a bulk action have all settled
  // — otherwise the next-pill counts are still stale and we'd fall through to
  // Backorder even when Send has work.
  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    if (bulkRefreshing) return;
    if (!workflowActive || !orders_loaded || orders.length > 0) return;
    const canHandle = isPicker || isAdmin;
    let target = "backorder";
    if (isApprovePreset && canHandle) {
      target =
        orders_ordered > 0
          ? HANDLE_PRESET_KEY
          : orders_inProgress > 0
            ? SEND_PRESET_KEY
            : "backorder";
    } else if (isHandlePreset) {
      target = orders_inProgress > 0 ? SEND_PRESET_KEY : "backorder";
    }
    history.replace(`/orders?tab=${SHOP_TAB.promo}&preset=${target}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orders_loaded,
    orders.length,
    workflowActive,
    redirectTarget,
    bulkRefreshing,
  ]);

  useEffect(() => {
    if (redirectTarget || presetNotAllowed) return;
    const fetchOnload = async () => {
      if (!orders_loaded) {
        await dispatch(fetchOrdersAsync());
        if (isHandlePreset) {
          await dispatch(fetchShippableOrdersAsync());
        }
        if (isApprovePreset) {
          await dispatch(fetchApprovableOrdersAsync());
        }
        if (isSendPreset) {
          await dispatch(fetchInProgressOrdersAsync());
        }
      }
    };
    fetchOnload();
  }, [
    orders_loaded,
    dispatch,
    isHandlePreset,
    isApprovePreset,
    isSendPreset,
    redirectTarget,
  ]);
  //#endregion

  const setPreset = (key: string) => {
    history.replace(`/orders?tab=${SHOP_TAB.promo}&preset=${key}`);
  };

  const pills = (
    <PromoPresetPills
      activeKey={presetKey}
      onSelect={setPreset}
      isPicker={isPicker}
      isAdmin={isAdmin}
      isHq={isHq}
    />
  );

  //#region RENDER
  return (
    <div
      aria-busy={bulkRefreshing}
      className={
        bulkRefreshing
          ? "pointer-events-none opacity-60 transition-opacity"
          : "transition-opacity"
      }
    >
      <OrdersContainer
        title={TAB_LABELS[SHOP_TAB.promo]}
        headerExtra={pills}
        baseParams={presetBaseParams}
        clientPaginate={bulkAvailable}
        hideOrdertypeFilter={!!filter.order_type}
        hideOrderstatusFilter={!!filter.order_status}
      />
    </div>
  );
  //#endregion
}
